import React, {useEffect, useState} from "react";
import CustomImageTable from "../../../../components/CustomImageTable";
import {apiInstance} from "../../../../Apis/Axios";
import {USER_SERVICE} from "../../../../reduxToolkit/EndPoints";
import IconAvatarTable from '../../../../assets/IconsTables/ic-avatar.svg';

/**
 * Selfies already requested, by user id in lowercase, the case the backend uses for the keys of the map
 * (the id of the simple search is a string read from SQL Server and can come in uppercase).
 * The image is not requested again when the page changes.
 */
const selfieCache = new Map();

/**
 * Users waiting for the next request, with the function that resolves their selfie.
 */
let pending = new Map();

/**
 * Requests in one call the selfies of every user asked for during the same render.
 * A failed request is removed from the cache so it can be retried later.
 */
const requestPendingSelfies = () => {
    const batch = pending;
    pending = new Map();
    // The avatars load in the background, the table is already visible //
    apiInstance.post(USER_SERVICE.getSelfies, [...batch.keys()], {overlay: false})
        .then(({data}) => {
            const selfies = data?.data || {};
            batch.forEach((resolve, userId) => resolve(selfies[userId] || null));
        })
        .catch(() => {
            batch.forEach((resolve, userId) => {
                selfieCache.delete(userId);
                resolve(null);
            });
        });
};

/**
 * Returns the selfie of the user, requesting it with the other rows of the table.
 * @param {string} userId - The id of the user.
 * @returns {Promise<string|null>} - The base64 image, or null when the user has no selfie.
 */
export const getSelfie = (userId) => {
    const key = userId.toLowerCase();
    if (!selfieCache.has(key)) {
        if (pending.size === 0) setTimeout(requestPendingSelfies, 0);
        selfieCache.set(key, new Promise((resolve) => pending.set(key, resolve)));
    }
    return selfieCache.get(key);
};

/**
 * Empties the selfies requested, so a selfie changed in other module is requested again
 * and the cache does not grow while the user navigates the application.
 */
export const clearSelfieCache = () => selfieCache.clear();

/**
 * Avatar of the user access log table. The list no longer brings the image, so each row
 * requests the selfie of its user after it is rendered.
 *
 * @param {string} userId - The id of the user.
 * @param {string} text - The name of the user.
 * @param {string} buttonImg - Identifier for the image.
 * @param {Function} setExpandImage - Opens the image in the expanded view.
 * @returns {JSX.Element}
 */
const UserAccessAvatar = ({userId, text, buttonImg, setExpandImage}) => {
    const [image, setImage] = useState(null);

    useEffect(() => {
        // The row can be reused by another user when the page changes, so the previous selfie is not shown meanwhile //
        setImage(null);
        if (!userId) return;
        let active = true;
        getSelfie(userId).then((selfie) => {
            if (active) setImage(selfie);
        });
        return () => { active = false; };
    }, [userId]);

    return (
        <CustomImageTable
            text={text}
            image={image ? `data:image/jpeg;base64,${image}` : IconAvatarTable}
            classNameImage={'rounded-circle'}
            buttonImg={buttonImg}
            click={(e, expanded) => {
                if (image) setExpandImage(expanded);
            }}
        />
    );
};

export default UserAccessAvatar;
